/* MIXED-VENDOR CART WARNING
   ---------------------------------------------------------------------------
   TCC is a distributor and holds no stock: every line ships from whichever
   manufacturer made it. A cart that mixes brands therefore carries three
   consequences the customer cannot see, in order of what they cost:

     1. FIT — the main one. Belts, posts, sign frames, cassettes and bases are
        engineered per brand and are mostly NOT cross-compatible. A Visiontron
        sign adapter does not fit a Queue Solutions post. The customer finds
        out after delivery, and it becomes a return.
     2. DOUBLE SHIPPING — separate warehouses mean separate freight, so the
        customer can be charged more than once.
     3. SPLIT DELIVERY — the parts arrive on different days.

   Shown on the cart page AND in the drawer rather than only at checkout: the
   fit problem is cheapest to fix before paying, and Shopify's checkout is not
   themeable on this plan anyway.

   WHY A SEPARATE FILE: assets/tcc-cart-notice.js used to carry a lot of
   hard-won documentation about the Webyze cart behaviour. That app is gone and
   the file was trimmed on 2026-08-31 to just its "continue shopping" feature,
   but this stays independent — the two do unrelated jobs. The slot logic below
   is deliberately a small duplicate of the one there; if the theme's cart
   markup ever changes, BOTH files need updating.

   VENDOR DATA: vendor is not in the cart DOM, so it comes from /cart.js, which
   returns a `vendor` per line item. Note this is only as good as the product
   data — 4 "Custom | QueuePro" products carried vendor "The Crowd Controller"
   until 2026-08-28 despite being Queue Solutions goods. If this notice ever
   fires on a cart that looks single-brand, suspect the vendor field first.

   ---------------------------------------------------------------------------
   NO INTERVAL (changed 2026-08-31). This used to run setInterval(tick, 900)
   site-wide, forever — the same pattern that came in with Webyze and was
   removed from tcc-cart-notice.js on the same day.

   It cannot simply be scoped to /cart, because the cart DRAWER opens on any
   page. Instead a MutationObserver watches for the DOM changing and ticks on
   a rAF debounce, so an idle page costs nothing at all and opening the drawer
   or changing a quantity still reacts immediately.

   The existing guards still apply on top: refresh() returns early when no cart
   surface is on screen, and /cart.js is only refetched when the line-up
   fingerprint changed or FETCH_MIN_MS elapsed. render() inserting the notice
   retriggers the observer once, then settles — the `if (existing) return`
   guard stops it looping. */
(function () {
  'use strict';
  if (window.__tccVendorNotice) return;
  window.__tccVendorNotice = true;

  var NOTICE_CLASS = 'tcc-cart-notice';          /* shared styling */
  var VENDOR_CLASS = 'tcc-vendor-notice';        /* this notice specifically */
  var CART_ITEM = '[class~="#cart-item"], [class~="#cart-drawer-product"]';
  var FETCH_MIN_MS = 4000;
  var POLL_MS = 900;                             /* fallback only, see watch() */

  var lastFetch = 0;
  var lastSig = null;
  var vendors = null;   /* null = not yet known, [] = single vendor */

  function noticeSlots() {
    var slots = [];
    var drawerBody = document.querySelector('[class~="#cart-drawer-body"]');
    if (drawerBody) {
      slots.push({ host: drawerBody, after: drawerBody.querySelector('[class~="#cart-drawer-header"]') });
    }
    var page = document.querySelector('[class~="#cart-items"]')
            || document.querySelector('[class~="#cart"]');
    if (page) slots.push({ host: page, after: null });
    return slots;
  }

  /* Cheap fingerprint of what is on screen, so an add/remove/quantity change
     refetches immediately but idle ticks do not. */
  function cartSignature() {
    var items = document.querySelectorAll(CART_ITEM);
    var parts = [];
    for (var i = 0; i < items.length; i++) {
      var a = items[i].querySelector('a[href*="/products/"]');
      parts.push((a && a.getAttribute('href')) || String(i));
    }
    return parts.join('|');
  }

  function refresh() {
    if (!noticeSlots().length) return;

    var sig = cartSignature();
    var now = Date.now();
    if (sig === lastSig && (now - lastFetch) < FETCH_MIN_MS) return;
    lastSig = sig;
    lastFetch = now;

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return;
        var seen = [];
        for (var i = 0; i < cart.items.length; i++) {
          var v = (cart.items[i].vendor || '').trim();
          if (v && seen.indexOf(v) === -1) seen.push(v);
        }
        vendors = seen;
        render();
      })
      .catch(function () { /* keep whatever is on screen if the fetch fails */ });
  }

  function message(list) {
    var names = list.slice(0, 4).join(', ');
    if (list.length > 4) names += ' and others';
    return '<strong>Your cart mixes ' + list.length + ' brands (' + names + ').</strong> ' +
      'Parts are usually brand-specific &mdash; belts, posts, sign frames and bases from ' +
      'different manufacturers often will not fit together. They also ship from separate ' +
      'warehouses, so you may be charged shipping more than once and receive them on ' +
      'different days. ' +
      '<a href="/pages/request-quote">Check compatibility with us</a> before ordering.';
  }

  function render() {
    var show = !!(vendors && vendors.length > 1);

    noticeSlots().forEach(function (slot) {
      var existing = slot.host.querySelector('.' + VENDOR_CLASS);

      if (!show) {
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
        return;
      }
      if (existing) return;

      var note = document.createElement('div');
      note.className = NOTICE_CLASS + ' ' + VENDOR_CLASS;
      note.setAttribute('role', 'status');
      note.innerHTML = message(vendors);

      /* Sits AFTER any other .tcc-cart-notice when both are present: this one
         is advisory, anything else there is likely time-critical. */
      var prior = slot.host.querySelector('.' + NOTICE_CLASS + ':not(.' + VENDOR_CLASS + ')');
      if (prior && prior.parentNode === slot.host) {
        slot.host.insertBefore(note, prior.nextSibling);
      } else if (slot.after && slot.after.parentNode === slot.host) {
        slot.host.insertBefore(note, slot.after.nextSibling);
      } else {
        slot.host.insertBefore(note, slot.host.firstChild);
      }
    });
  }

  function tick() { refresh(); render(); }

  /* React to the cart drawer opening or the cart re-rendering, without a timer.
     Falls back to the old poll only where MutationObserver is unavailable. */
  function watch() {
    if (typeof MutationObserver !== 'function') { setInterval(tick, POLL_MS); return; }

    var queued = false;
    var run = function () { queued = false; tick(); };
    var schedule = function () {
      if (queued) return;
      queued = true;
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
      else setTimeout(run, 50);
    };

    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });

    /* Belt and braces: the theme's own cart events, if it emits them. */
    document.addEventListener('cart:updated', schedule);
    document.addEventListener('cart:refresh', schedule);
  }

  function start() { tick(); watch(); }

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
